'use client';

import { c as _c } from "react/compiler-runtime";
import { type FactiiiTag } from '@shared/all/domains/factiiis';
import { convertToFactiiiTag } from '@shared/utils/helpers/formatNumbers';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useQuery } from '@tanstack/react-query';
import { Loader, Plus, Search } from 'lucide-react';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { client, type RouterOutputs } from '../../../utils/trpc';
import Button from '../../common/Button';
import Input from '../../UI/Input';
import { FactiiiSearchResult } from '../FactiiiSearchResult';
import CreateSourceFactiii from './CreateSourceFactiii';
type Props = {
  myPostFactiiiCounts: FactiiiTag[];
  setMyPostFactiiiCounts: Dispatch<SetStateAction<FactiiiTag[]>>;
  handleClick: (tag: FactiiiTag) => void;
};
export type SourceItem = RouterOutputs['search']['factiiis']['factiiis'][number] & {
  url?: string;
};
export const SourceFactiii = t0 => {
  const $ = _c(39);
  const {
    myPostFactiiiCounts,
    setMyPostFactiiiCounts,
    handleClick
  } = t0;
  const [createSourceOpen, setCreateSourceOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  let t1;
  if ($[0] !== searchQuery) {
    t1 = ["sourceFactiiiSearch", searchQuery];
    $[0] = searchQuery;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== myPostFactiiiCounts || $[3] !== searchQuery) {
    t2 = () => unwrapFromResult(client.search.factiiis.query({
      query: searchQuery,
      requirements: ["HUMAN_SOURCE", "GOVERNMENT_SOURCE", "ENTERPRISE_SOURCE", "ANONYMOUS_SOURCE"],
      ignoredIds: myPostFactiiiCounts.map(_temp)
    }));
    $[2] = myPostFactiiiCounts;
    $[3] = searchQuery;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      placeholderData: _temp2,
      staleTime: 30000
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    data: searchResults,
    isLoading: searchLoading
  } = useQuery(t3);
  let t4;
  if ($[8] !== myPostFactiiiCounts || $[9] !== setMyPostFactiiiCounts) {
    t4 = sourceItem => {
      if (myPostFactiiiCounts.some(s_0 => s_0.factiiiId === sourceItem.id)) {
        return alert("BUGZ: Source already added");
      }
      setMyPostFactiiiCounts(prev => [...prev, convertToFactiiiTag(sourceItem)]);
    };
    $[8] = myPostFactiiiCounts;
    $[9] = setMyPostFactiiiCounts;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const addSource = t4;
  let t5;
  if ($[11] !== searchLoading) {
    t5 = [searchLoading ? <Loader key="loading-icon" className="animate-spin" /> : <Search key="search-icon" />];
    $[11] = searchLoading;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== searchQuery || $[14] !== t5) {
    t6 = <Input value={searchQuery} onChange={setSearchQuery} placeholder="Search for sources..." autofocus={true} icons={t5} />;
    $[13] = searchQuery;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== addSource || $[17] !== createSourceOpen || $[18] !== searchQuery) {
    t7 = createSourceOpen && <CreateSourceFactiii factiiiName={searchQuery} closeHandler={() => setCreateSourceOpen(false)} addSource={addSource} />;
    $[16] = addSource;
    $[17] = createSourceOpen;
    $[18] = searchQuery;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== handleClick || $[21] !== myPostFactiiiCounts || $[22] !== searchLoading || $[23] !== searchResults?.factiiis) {
    bb0: {
      let t9;
      if ($[25] !== myPostFactiiiCounts) {
        t9 = new Set(myPostFactiiiCounts.map(_temp3));
        $[25] = myPostFactiiiCounts;
        $[26] = t9;
      } else {
        t9 = $[26];
      }
      const selectedIds = t9;
      const filtered = searchResults?.factiiis?.filter(f => !selectedIds.has(f.id)) ?? [];
      if (filtered.length > 0) {
        let t10;
        if ($[27] !== handleClick) {
          t10 = searchResult => <FactiiiSearchResult key={searchResult.id} tag={convertToFactiiiTag(searchResult)} replyConfig={searchResult.replyConfig} handleClick={() => handleClick(convertToFactiiiTag(searchResult))} />;
          $[27] = handleClick;
          $[28] = t10;
        } else {
          t10 = $[28];
        }
        t8 = filtered.map(t10);
        break bb0;
      }
      if (searchLoading) {
        let t10;
        if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <div className="flex items-center justify-center py-4"><Loader className="animate-spin text-textSecondary" /></div>;
          $[29] = t10;
        } else {
          t10 = $[29];
        }
        t8 = t10;
        break bb0;
      }
      if (searchResults?.factiiis.length === 0 || filtered.length === 0) {
        let t10;
        if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <p className="mb-2 text-center text-sm text-textSecondary">No sources found, click below to add one...</p>;
          $[30] = t10;
        } else {
          t10 = $[30];
        }
        let t11;
        if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = () => setCreateSourceOpen(true);
          $[31] = t11;
        } else {
          t11 = $[31];
        }
        let t12;
        if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
          t12 = <div className="py-4">{t10}<Button onClick={t11} text="Add New Source Factiii" icon={<Plus />} classNames="w-full" /></div>;
          $[32] = t12;
        } else {
          t12 = $[32];
        }
        t8 = t12;
        break bb0;
      }
      t8 = null;
    }
    $[20] = handleClick;
    $[21] = myPostFactiiiCounts;
    $[22] = searchLoading;
    $[23] = searchResults?.factiiis;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[33] !== t8) {
    t9 = <div className="mt-3 space-y-3">{t8}</div>;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== t6 || $[36] !== t7 || $[37] !== t9) {
    t10 = <div className="flex flex-col">{t6}{t7}{t9}</div>;
    $[35] = t6;
    $[36] = t7;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
};
function _temp(s) {
  return s.factiiiId;
}
function _temp2(previousData) {
  return previousData;
}
function _temp3(s_1) {
  return s_1.factiiiId;
}